/**
 * hmrcErrorParsing.js
 *
 * Shared parsing for HMRC's structured error shapes, used by both
 * VATSubmission.jsx (live submit-time rejections) and
 * VatSubmissionHistory.js (stored rejection reasons on past submissions).
 *
 * HMRC's error body shape varies:
 *   - { code, message }
 *   - { errors: [ { code, message }, ... ] }
 *   - occasionally just a raw string
 *
 * parseHmrcErrorDetail() is the single source of truth for reading that
 * shape. It operates on an already-parsed object (or null/undefined) and
 * returns the primary { code, message } pair, or null if nothing usable
 * was found. It does NOT JSON.parse — callers that have a stringified
 * value (e.g. status_message from the backend) parse it first and pass
 * the resulting object in.
 */

export const parseHmrcErrorDetail = (details) => {
    if (!details) return null;

    if (Array.isArray(details?.errors) && details.errors.length > 0) {
        const primary = details.errors[0];
        return {
            code: primary?.code || null,
            message: primary?.message || primary?.code || null
        };
    }

    if (details?.message || details?.code) {
        return {
            code: details.code || null,
            message: details.message || details.code || null
        };
    }

    return null;
};

// Specific, non-generic copy for error codes where HMRC's literal message
// is misleading on its own. DUPLICATE_SUBMISSION reads like a transient
// failure inviting another attempt, when actually no resubmission of this
// period will ever succeed while it remains Accepted at HMRC (see
// SalonLogic_VAT_Session_Handover_Continuation, Section 3).
//
// Do not add further codes here without the same explicit checking the
// handover docs call for — these messages assert something about what
// HMRC will/won't accept, which is compliance-relevant.
const SPECIFIC_MESSAGES = {
    DUPLICATE_SUBMISSION:
        "HMRC already has an accepted VAT return on file for this period. " +
        "Resubmitting won't go through — if you need to correct figures that " +
        "were already accepted, contact support rather than submitting again."
};

// For display: given a parsed { code, message } (or null), return the
// best user-facing string — a specific override if one exists for the
// code, otherwise HMRC's own message, otherwise null.
export const getDisplayMessage = (detail) => {
    if (!detail) return null;
    if (detail.code && SPECIFIC_MESSAGES[detail.code]) {
        return SPECIFIC_MESSAGES[detail.code];
    }
    return detail.message || detail.code || null;
};